import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {catchError, map, Observable, of, shareReplay, tap} from 'rxjs';
import {apiUrl} from '../../environments/environment';
import {
  PersistentTableFilter,
  RestoredTableState,
  TableModule,
  TableSort,
  TableStateConfig,
  UserTablePreference
} from '../_models/table-preference';

interface StoredTableSessionState {
  pageIndex?: number;
  filters?: Record<string, unknown>;
  scrollTop?: number;
}

@Injectable({providedIn: 'root'})
export class TableViewStateService {
  private static readonly SESSION_PREFIX = 'colorjob.table-state.v1.';
  private static readonly ACTIVE_USER_KEY = 'colorjob.table-state.active-user';
  private static readonly ACTIVE_SECTION_PREFIX = 'colorjob.active-section.v1.';

  private readonly preferences = new Map<TableModule, UserTablePreference>();
  private loadedUserId: number | null = null;
  private loadingUserId: number | null = null;
  private loadRequest$: Observable<void> | null = null;

  constructor(private readonly http: HttpClient) {
  }

  restore<TFilters extends object>(
    userId: number,
    config: TableStateConfig<TFilters>
  ): Observable<RestoredTableState<TFilters>> {
    return this.ensureLoaded(userId).pipe(
      map(() => {
        const preference = this.preferences.get(config.module);
        const session = this.readSessionState(userId, config.module);
        const useAccountDisplay = config.persistDisplayOnResponsiveViews === true
          || window.innerWidth > 1279;

        const savedSortKey = preference?.sortColumn ?? null;
        const sorting: TableSort | null = useAccountDisplay
          && savedSortKey
          && preference?.sortDirection
          && config.allowedSortKeys.includes(savedSortKey)
          ? {
              key: savedSortKey,
              direction: preference.sortDirection.toLowerCase() as 'asc' | 'desc'
            }
          : null;

        const savedPageSize = config.persistPageSize === false ? null : preference?.pageSize;
        const pageSize = useAccountDisplay
          && savedPageSize
          && [20, 50, 100].includes(savedPageSize)
          ? savedPageSize
          : (config.defaultPageSize ?? 50);

        const filters: Partial<TFilters> = {};
        for (const key of config.sessionFilterKeys) {
          if (session.filters && Object.prototype.hasOwnProperty.call(session.filters, String(key))) {
            filters[key] = session.filters[String(key)] as TFilters[typeof key];
          }
        }
        for (const [filterKey, persistentFilter] of Object.entries(
          config.persistentFilterBindings ?? {}
        )) {
          filters[filterKey as keyof TFilters] = !!preference?.persistentFilters?.[
            persistentFilter as PersistentTableFilter
          ] as TFilters[keyof TFilters];
        }

        return {
          sorting,
          pageIndex: this.validPageIndex(session.pageIndex),
          pageSize,
          filters,
          scrollTop: this.validScrollTop(session.scrollTop)
        };
      })
    );
  }

  saveDisplay<TFilters extends object>(
    userId: number,
    config: TableStateConfig<TFilters>,
    sorting: TableSort | null,
    pageSize: number
  ): Observable<UserTablePreference | null> {
    if (config.persistDisplayOnResponsiveViews !== true && window.innerWidth <= 1279) {
      return of(null);
    }

    return this.http.put<UserTablePreference>(
      `${apiUrl}/user/table-preferences/${config.module}/display`,
      {
        sortColumn: sorting?.key ?? null,
        sortDirection: sorting?.direction.toUpperCase() ?? null,
        pageSize: config.persistPageSize === false ? null : pageSize
      },
      {withCredentials: true}
    ).pipe(tap(preference => this.preferences.set(preference.module, preference)));
  }

  savePersistentFilter(
    module: TableModule,
    filter: PersistentTableFilter,
    value: boolean
  ): Observable<UserTablePreference> {
    return this.http.put<UserTablePreference>(
      `${apiUrl}/user/table-preferences/${module}/filters/${filter}`,
      {value},
      {withCredentials: true}
    ).pipe(tap(preference => this.preferences.set(preference.module, preference)));
  }

  saveSessionState<TFilters extends object>(
    userId: number,
    config: TableStateConfig<TFilters>,
    pageIndex: number,
    filters: TFilters,
    scrollTop?: number
  ): void {
    const storedFilters: Record<string, unknown> = {};
    for (const key of config.sessionFilterKeys) {
      const value = filters[key];
      if (value !== undefined) {
        storedFilters[String(key)] = value;
      }
    }

    const state: StoredTableSessionState = {
      pageIndex: this.validPageIndex(pageIndex),
      filters: storedFilters,
      scrollTop: this.validScrollTop(scrollTop)
    };
    try {
      sessionStorage.setItem(this.sessionKey(userId, config.module), JSON.stringify(state));
    } catch {
      // Corrupted or unavailable browser storage must not block table navigation.
    }
  }

  clearForLogout(): void {
    this.clearStoredTableStates();
    sessionStorage.removeItem(TableViewStateService.ACTIVE_USER_KEY);
    this.preferences.clear();
    this.loadedUserId = null;
    this.loadingUserId = null;
    this.loadRequest$ = null;
  }

  getActiveSectionId(userId: number, projectId: number): number | null | undefined {
    try {
      const value = sessionStorage.getItem(this.activeSectionKey(userId, projectId));
      if (value === null) return undefined;
      if (value === 'all') return null;
      const id = Number(value);
      return Number.isFinite(id) && id > 0 ? id : undefined;
    } catch {
      return undefined;
    }
  }

  setActiveSectionId(userId: number, projectId: number, sectionId: number | null): void {
    try {
      sessionStorage.setItem(this.activeSectionKey(userId, projectId), sectionId ? String(sectionId) : 'all');
    } catch {}
  }

  private ensureLoaded(userId: number): Observable<void> {
    this.activateUser(userId);
    if (this.loadedUserId === userId) {
      return of(void 0);
    }
    if (this.loadingUserId === userId && this.loadRequest$) {
      return this.loadRequest$;
    }

    this.loadingUserId = userId;
    this.loadRequest$ = this.http.get<UserTablePreference[]>(
      `${apiUrl}/user/table-preferences`,
      {withCredentials: true}
    ).pipe(
      catchError(() => of([])),
      tap(preferences => {
        this.preferences.clear();
        preferences.forEach(preference => this.preferences.set(preference.module, preference));
        this.loadedUserId = userId;
        this.loadingUserId = null;
      }),
      map((): void => undefined),
      shareReplay(1)
    );
    return this.loadRequest$;
  }

  private activateUser(userId: number): void {
    const activeUserId = sessionStorage.getItem(TableViewStateService.ACTIVE_USER_KEY);
    if (activeUserId && activeUserId !== String(userId)) {
      this.clearStoredTableStates();
    }
    sessionStorage.setItem(TableViewStateService.ACTIVE_USER_KEY, String(userId));
    if (this.loadedUserId !== null && this.loadedUserId !== userId) {
      this.preferences.clear();
      this.loadedUserId = null;
      this.loadingUserId = null;
      this.loadRequest$ = null;
    }
  }

  private readSessionState(userId: number, module: TableModule): StoredTableSessionState {
    try {
      const raw = sessionStorage.getItem(this.sessionKey(userId, module));
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      sessionStorage.removeItem(this.sessionKey(userId, module));
      return {};
    }
  }

  private clearStoredTableStates(): void {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(TableViewStateService.SESSION_PREFIX)) {
        sessionStorage.removeItem(key);
      }
      if (key?.startsWith(TableViewStateService.ACTIVE_SECTION_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    }
  }

  private sessionKey(userId: number, module: TableModule): string {
    return `${TableViewStateService.SESSION_PREFIX}${userId}.${module}`;
  }

  private activeSectionKey(userId: number, projectId: number): string {
    return `${TableViewStateService.ACTIVE_SECTION_PREFIX}${userId}.${projectId}`;
  }

  private validPageIndex(value: unknown): number {
    return Number.isInteger(value) && Number(value) >= 0 ? Number(value) : 0;
  }

  private validScrollTop(value: unknown): number {
    const num = Number(value);
    return Number.isFinite(num) && num >= 0 ? Math.round(num) : 0;
  }
}
